/**
 * useLayoutState - State management and keyboard shortcuts for DashboardLayout
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useSidebar } from '../../hooks/useSidebar'
import {
  GLOBAL_SEARCH_OPEN_EVENT,
  getOpenGlobalSearchDetail,
} from '../global-search/openGlobalSearch'
import { getActiveFindController } from '../find/findScopeRegistry'

interface UseLayoutStateOptions {
  quietMode: boolean
  showGlobalSearch: boolean
  showCommandPalette: boolean
  showSettingsModal: boolean
  showAISidebar: boolean
  externalSettingsOpen?: boolean
  onOpenSettings?: (tabId?: string) => void
  onCloseSettings?: () => void
  /**
   * Optional per-hub sidebar collapse namespace + smart default. See
   * useSidebar.ts for the touched-sentinel semantics. Without these
   * opts the hook stays on the legacy global `sidebar-collapsed`
   * key so existing hubs see zero behaviour change.
   */
  sidebarHubId?: string
  sidebarDefaultCollapsed?: boolean
  /**
   * BUG-2026-05-21-SIDEBAR-DOES-NOT-AUTOCOLLAPSE-ON-CANVAS-ROUTES —
   * route prefixes that should ephemerally collapse the sidebar so
   * canvas-heavy surfaces (Kanban, Calendar, full-screen email
   * thread) get the screen real estate they need without the
   * operator manually clicking the chevron every time.
   *
   * Pass the current pathname in `currentPathname`. When it starts
   * with any prefix in this list, the returned `userCollapsed` is
   * forced to `true` regardless of stored preference. NO localStorage
   * write happens, so the operator's preference is preserved for the
   * moment they leave the canvas route.
   *
   * Hosts that want the legacy behaviour just omit both opts.
   */
  autoCollapseRoutePrefixes?: ReadonlyArray<string>
  currentPathname?: string
  /**
   * FEAT-2026-06-03-MASTER-SIDEBAR-VIEWPORT-AUTOCOLLAPSE — when true,
   * the rail auto-collapses on narrower desktops (viewport < Tailwind
   * `xl` / 1280px) and auto-expands at ≥ xl, mirroring the tenant
   * shells' `useSidebarAutoCollapse`. An operator-driven toggle pins
   * their choice for the session. Off (default) → legacy behaviour
   * (stored preference + route override only). See DashboardLayout,
   * which defaults the prop to true so every hub gets SAM parity.
   */
  autoCollapseBelowXl?: boolean
}

export function useLayoutState(opts: UseLayoutStateOptions) {
  const { resolvedTheme, toggle: toggleTheme } = useTheme()
  const { collapsed: storedCollapsed, setCollapsed: setUserCollapsed } = useSidebar({
    hubId: opts.sidebarHubId,
    defaultCollapsed: opts.sidebarDefaultCollapsed,
  })

  // BUG-2026-05-21-SIDEBAR-DOES-NOT-AUTOCOLLAPSE-ON-CANVAS-ROUTES —
  // Compute the route-driven override WITHOUT writing to storage.
  // Two design rules:
  //   1. Ephemeral only — leaving the canvas restores whatever the
  //      operator's persisted preference was.
  //   2. Doesn't stamp the touched sentinel — the operator's per-
  //      hub default-collapsed semantics in useSidebar.ts are
  //      untouched.
  // The override is OR'd with the stored value: if the operator
  // already collapsed the rail, we don't need to force it; if the
  // route says "canvas heavy", we collapse regardless.
  const isOnAutoCollapseRoute = useMemo(() => {
    const prefixes = opts.autoCollapseRoutePrefixes
    const pathname = opts.currentPathname
    if (!prefixes || prefixes.length === 0 || !pathname) return false
    return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  }, [opts.autoCollapseRoutePrefixes, opts.currentPathname])

  // FEAT-2026-06-03-MASTER-SIDEBAR-VIEWPORT-AUTOCOLLAPSE — viewport
  // band tracking, ported from the tenant shells' useSidebarAutoCollapse.
  // The desktop rail (rendered `hidden lg:block` by DashboardLayout) is
  // visible from lg up; below xl (1280px) we auto-collapse it to the
  // 76px icon dock so narrower desktops + split-screen reclaim content
  // width, and re-expand at ≥ xl. matchMedia is guarded the same way as
  // isSmUp/isLgUp below (SSR-safe default = expanded). Off unless the
  // host opts in via `autoCollapseBelowXl`.
  const [isXlUp, setIsXlUp] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 1280px)').matches
      : true,
  )

  // Session pin. Any operator-driven collapse/expand (footer chevron
  // or ⌘B, both routed through the returned setUserCollapsed) flips
  // this so the viewport override stops fighting their explicit choice
  // until reload. Mirrors the tenant shells' `sidebarUserPreference`,
  // minus cross-reload persistence — useSidebar's stored value already
  // persists the collapsed case across reloads. Must be STATE not a
  // ref: when stored is already false and the operator clicks expand,
  // setUserCollapsed(false) is a no-op (no re-render), so a ref flip
  // would never surface — the rail would stay stuck collapsed.
  const [sidebarPinned, setSidebarPinned] = useState(false)

  const viewportAutoCollapsed = !!opts.autoCollapseBelowXl && !isXlUp
  // BUG-2026-06-03-CANVAS-ROUTE-TRAPS-EXPAND — an explicit operator
  // pin must win over BOTH the viewport band AND the canvas-route
  // override. Previously `isOnAutoCollapseRoute` was OR'd ahead of the
  // pin check, so on a flush-fill route (e.g. /emails, /kanban)
  // clicking the expand chevron updated the stored preference but
  // `userCollapsed` stayed forced `true` — the rail could never be
  // expanded on those routes. Now: a pinned operator gets exactly
  // their stored preference everywhere; an unpinned operator still
  // gets the ephemeral route/viewport auto-collapse (no storage write,
  // so leaving the route restores their preference untouched).
  const userCollapsed = sidebarPinned
    ? storedCollapsed
    : isOnAutoCollapseRoute || storedCollapsed || viewportAutoCollapsed

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const [drawerOpenMode, setDrawerOpenMode] = useState<'hover' | 'click' | null>(null)
  const [internalSettingsOpen, setInternalSettingsOpen] = useState(false)
  // Null means "use the host default". A menu click sets a real tab id so
  // the modal opens on that section instead of always landing on Profile.
  const [settingsTab, setSettingsTab] = useState<string | null>(null)
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false)
  // BUG-2026-05-27-KANBAN-SEARCH-NOT-BOARD-SCOPED — held alongside
  // `globalSearchOpen` so the palette opens with the producer's
  // pre-filled query + filter chips. Both are CLEARED in
  // `handleCloseGlobalSearch` below so manual ⌘F re-opens start
  // blank — otherwise the same seeded query would leak into the
  // next open. See `packages/ui/src/components/global-search/
  // openGlobalSearch.ts` for the event contract.
  const [globalSearchInitialQuery, setGlobalSearchInitialQuery] = useState<string | undefined>(undefined)
  const [globalSearchInitialActiveFilters, setGlobalSearchInitialActiveFilters] = useState<string[] | undefined>(undefined)
  const [aiSidebarOpen, setAISidebarOpen] = useState(false)
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false)

  const [isSmUp, setIsSmUp] = useState(() => typeof window !== 'undefined' ? window.matchMedia('(min-width: 640px)').matches : true)
  const [isLgUp, setIsLgUp] = useState(() => typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true)

  const settingsOpen = opts.externalSettingsOpen !== undefined ? opts.externalSettingsOpen : internalSettingsOpen

  // Auto-collapse sidebar in quiet mode. Uses the RAW setter (not the
  // pinned wrapper) — quiet mode is an automatic surface, not an
  // operator-driven preference, so it must not pin against the
  // viewport auto-collapse.
  useEffect(() => { if (opts.quietMode) setUserCollapsed(true) }, [opts.quietMode, setUserCollapsed])

  // FEAT-2026-06-03-MASTER-SIDEBAR-VIEWPORT-AUTOCOLLAPSE — the setter
  // exposed to the Sidebar's collapse chevron / ⌘B. Flips the session
  // pin so an explicit operator choice survives viewport changes, then
  // delegates to useSidebar's persisting setter.
  const setUserCollapsedPinned = useCallback(
    (value: boolean) => {
      setSidebarPinned(true)
      setUserCollapsed(value)
    },
    [setUserCollapsed],
  )

  // The quote sheet asks the hub rail to collapse when the operator hides
  // the tools rail. Same setter as the chevron and Ctrl+B.
  useEffect(() => {
    const onSetCollapsed = (event: Event) => {
      const collapsed = (event as CustomEvent<{ collapsed?: boolean }>).detail?.collapsed
      if (typeof collapsed === 'boolean') setUserCollapsedPinned(collapsed)
    }
    window.addEventListener('quotech:set-sidebar-collapsed', onSetCollapsed)
    return () => window.removeEventListener('quotech:set-sidebar-collapsed', onSetCollapsed)
  }, [setUserCollapsedPinned])

  // Responsive breakpoints
  useEffect(() => {
    if (typeof window === 'undefined') return
    const sm = window.matchMedia('(min-width: 640px)')
    const lg = window.matchMedia('(min-width: 1024px)')
    const xl = window.matchMedia('(min-width: 1280px)')
    const update = () => { setIsSmUp(sm.matches); setIsLgUp(lg.matches); setIsXlUp(xl.matches) }
    update()
    sm.addEventListener('change', update)
    lg.addEventListener('change', update)
    xl.addEventListener('change', update)
    return () => { sm.removeEventListener('change', update); lg.removeEventListener('change', update); xl.removeEventListener('change', update) }
  }, [])

  // Close mobile drawer when crossing to lg+
  useEffect(() => { if (isLgUp && mobileDrawerOpen) setMobileDrawerOpen(false) }, [isLgUp, mobileDrawerOpen])

  // Keyboard shortcuts — universal search opens on ⌘F / Ctrl+F (primary)
  // and ⌘K / Ctrl+K (legacy alias). Capture phase + preventDefault so
  // browser Find-in-page never steals the chord on canvas routes
  // (Kanban, jobs, email) where operators expect in-app search.
  //
  // SINGLE ⌘F AUTHORITY (BUG-2026-06-10-CMDF-POPUP-SHADOWED): this is
  // the one capture-phase handler that owns the chord on the master
  // shell. On ⌘F it first consults the in-page find registry — if a
  // mounted surface (email reader, kanban board, cards+emails popup)
  // has claimed "find here", we open that instead of the palette. ⌘K
  // always opens the global palette ("⌘F = right here, ⌘K = anywhere").
  // The old per-surface bubble-phase bindings were shadowed by this
  // handler's stopPropagation(); they now register controllers instead.
  //
  // UNIVERSAL-SHORTCUTS PHASE 2a: ⌘, (settings) was MIGRATED off this
  // handler onto the central dispatcher (ShortcutProvider →
  // GlobalShortcutBindings registering `app.settings.toggle`, wired to
  // `openSettings` below) so it respects user remap/disable overrides.
  // ⌘F / ⌘K stay here because they need capture-phase + stopPropagation
  // (single-⌘F authority); ⌘I stays until its descriptor lands (Phase 2b).
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey) {
      const key = e.key.toLowerCase()
      if (key === 'f' || key === 'k') {
        e.preventDefault()
        e.stopPropagation()
        if (key === 'f') {
          const controller = getActiveFindController()
          if (controller) {
            controller.open()
            return
          }
        }
        if (opts.showGlobalSearch) setGlobalSearchOpen(true)
        else if (opts.showCommandPalette) setCommandPaletteOpen(true)
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
      e.preventDefault()
      if (opts.showAISidebar) setAISidebarOpen((prev) => !prev)
    }
  }, [opts])

  // ⌘, target (app.settings.toggle) — the dispatcher fires this via
  // GlobalShortcutBindings. Mirrors the legacy branch exactly: prefer the
  // host's controlled open callback, else flip the internal modal state.
  // Registration is gated on showSettingsModal by the caller, so this is
  // only ever invoked when a settings modal exists.
  const openSettings = useCallback((tabId?: string) => {
    setSettingsTab(typeof tabId === 'string' && tabId.length > 0 ? tabId : null)
    if (opts.externalSettingsOpen !== undefined) opts.onOpenSettings?.(tabId)
    else setInternalSettingsOpen(true)
  }, [opts])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, { capture: true })
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true })
  }, [handleKeyDown])

  // BUG-2026-05-27-KANBAN-SEARCH-NOT-BOARD-SCOPED — listen for the
  // cross-host "open global search with this query" event. Producers
  // (today: the kanban view's empty-board fallback CTA) dispatch
  // `window.dispatchEvent(new CustomEvent('quotech:open-global-
  // search', { detail: { query, activeFilters } }))`. We store the
  // detail in local state and open the palette; the next render
  // hands the detail to <GlobalSearch> via initialQuery /
  // initialActiveFilters. State is cleared in `handleCloseGlobalSearch`
  // so manual re-opens start blank.
  useEffect(() => {
    if (!opts.showGlobalSearch) return
    const handler = (ev: Event) => {
      const detail = getOpenGlobalSearchDetail(ev)
      if (!detail) return
      setGlobalSearchInitialQuery(detail.query)
      setGlobalSearchInitialActiveFilters(detail.activeFilters)
      setGlobalSearchOpen(true)
    }
    window.addEventListener(GLOBAL_SEARCH_OPEN_EVENT, handler)
    return () => window.removeEventListener(GLOBAL_SEARCH_OPEN_EVENT, handler)
  }, [opts.showGlobalSearch])

  // Wrapper close-handler that also clears the seeded initial state
  // so the next manual open (⌘F / topbar Search) starts blank
  // instead of re-applying the previous board-fallback query.
  const handleCloseGlobalSearch = useCallback(() => {
    setGlobalSearchOpen(false)
    setGlobalSearchInitialQuery(undefined)
    setGlobalSearchInitialActiveFilters(undefined)
  }, [])

  const handleOpenSearch = useCallback(() => {
    opts.showGlobalSearch ? setGlobalSearchOpen(true) : opts.showCommandPalette && setCommandPaletteOpen(true)
  }, [opts.showGlobalSearch, opts.showCommandPalette])

  const handleOpenNotifications = useCallback(() => setNotificationCenterOpen(true), [])

  const closeSettings = useCallback(() => {
    opts.onCloseSettings ? opts.onCloseSettings() : setInternalSettingsOpen(false)
  }, [opts.onCloseSettings])

  return {
    resolvedTheme, toggleTheme,
    userCollapsed, setUserCollapsed: setUserCollapsedPinned,
    commandPaletteOpen, setCommandPaletteOpen,
    mobileDrawerOpen, setMobileDrawerOpen,
    drawerOpenMode, setDrawerOpenMode,
    globalSearchOpen, setGlobalSearchOpen,
    /** BUG-2026-05-27-KANBAN-SEARCH-NOT-BOARD-SCOPED — see the
     *  state declarations + the event listener above. Forwarded to
     *  <GlobalSearch> in DashboardLayout. */
    globalSearchInitialQuery,
    globalSearchInitialActiveFilters,
    handleCloseGlobalSearch,
    aiSidebarOpen, setAISidebarOpen,
    notificationCenterOpen, setNotificationCenterOpen,
    settingsOpen, settingsTab, openSettings, closeSettings,
    isSmUp, isLgUp,
    handleOpenSearch, handleOpenNotifications,
  }
}
